import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Instagram, Loader2, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function InstagramPage() {
  const [configured, setConfigured] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [currentUsername, setCurrentUsername] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sessionid, setSessionid] = useState("");
  const [mode, setMode] = useState("sessionid");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/instagram");
      setConfigured(r.data.configured);
      setCurrentUsername(r.data.username || "");
      setHasPassword(!!r.data.has_password);
      setHasSession(!!r.data.has_sessionid);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!username) return toast.error("Username required");
    if (mode === "password" && !password) return toast.error("Password required");
    if (mode === "sessionid" && !sessionid) return toast.error("Sessionid required");
    setSaving(true);
    try {
      const body = { username };
      if (mode === "password") body.password = password;
      if (mode === "sessionid") body.sessionid = sessionid.trim();
      await api.post("/admin/instagram", body);
      toast.success("Instagram credentials saved");
      setPassword("");
      setSessionid("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete("/admin/instagram");
      toast.success("Credentials removed");
      setUsername("");
      setPassword("");
      setSessionid("");
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
    setDeleting(false);
  };

  return (
    <div className="px-4 py-5 sm:p-8 max-w-3xl">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Instagram authentication</h1>
      <p className="text-[#6A6F5C] text-sm mt-1 mb-6">
        Needed so we can fetch posts from Instagram profile sources. Credentials are stored in your private database.
      </p>

      <div className="bg-white border border-[rgba(61,64,53,0.08)] rounded-xl p-4 shadow-sm sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#E07A5F] to-[#F2CC8F] flex items-center justify-center text-white">
            <Instagram size={18} />
          </div>
          <div>
            <div className="font-heading font-semibold">Session credentials</div>
            <div className="text-xs text-[#6A6F5C]" data-testid="ig-status">
              {configured
                ? `Configured as @${currentUsername} · ${hasSession ? "sessionid" : ""}${hasSession && hasPassword ? " + " : ""}${hasPassword ? "password" : ""}`
                : "Not configured"}
            </div>
          </div>
        </div>

        <Tabs value={mode} onValueChange={setMode} className="mb-4">
          <TabsList className="h-auto w-full flex-col bg-[#F7F5F0] sm:w-auto sm:flex-row">
            <TabsTrigger value="sessionid" data-testid="tab-sessionid">
              <KeyRound size={14} className="mr-1.5" /> Sessionid (recommended)
            </TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password">
              <ShieldCheck size={14} className="mr-1.5" /> Username & password
            </TabsTrigger>
          </TabsList>
          <TabsContent value="sessionid" className="text-xs text-[#6A6F5C] leading-relaxed pt-3">
            Instagram blocks server-side password logins with a "Checkpoint required" challenge.
            Pasting a fresh <strong>sessionid</strong> cookie from your browser bypasses this completely.
            <div className="mt-2 p-3 bg-[#F7F5F0] rounded-md">
              <strong className="text-[#3D4035]">How to get your sessionid:</strong>
              <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                <li>Open <a href="https://www.instagram.com" target="_blank" rel="noreferrer" className="text-[#E07A5F] underline">instagram.com</a> in your browser and log in.</li>
                <li>Open DevTools (<kbd className="px-1 bg-white border rounded text-[10px]">F12</kbd>) → <strong>Application</strong> → <strong>Cookies</strong> → <em>https://www.instagram.com</em>.</li>
                <li>Copy the value of the <code className="px-1 bg-white border rounded text-[10px]">sessionid</code> cookie and paste it below.</li>
              </ol>
            </div>
          </TabsContent>
          <TabsContent value="password" className="text-xs text-[#6A6F5C] leading-relaxed pt-3">
            Less reliable — Instagram often requires a manual security checkpoint before allowing
            server-side password logins. Use a dedicated throwaway account if you go this route.
          </TabsContent>
        </Tabs>

        <form onSubmit={save} className="space-y-4">
          <div>
            <Label>Instagram username</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="yourhandle (no @)"
              required
              data-testid="ig-username"
            />
          </div>

          {mode === "password" ? (
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="ig-password"
              />
            </div>
          ) : (
            <div>
              <Label>sessionid cookie value</Label>
              <Textarea
                value={sessionid}
                onChange={(e) => setSessionid(e.target.value)}
                placeholder="e.g. 12345678%3AaBcDeFg%3A12%3AAYf..."
                rows={3}
                className="font-mono text-xs"
                data-testid="ig-sessionid"
              />
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="submit" disabled={saving} className="w-full bg-[#3D4035] hover:bg-[#2c2f25] sm:w-auto" data-testid="ig-save-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save credentials
            </Button>
            {configured && (
              <Button type="button" variant="outline" onClick={() => setConfirmOpen(true)} className="w-full text-[#D95D39] sm:w-auto" data-testid="ig-delete-btn">
                <Trash2 size={14} className="mr-1.5" /> Remove
              </Button>
            )}
          </div>
        </form>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove Instagram credentials?</AlertDialogTitle>
            <AlertDialogDescription>
              Stored Instagram session credentials will be deleted. Future extractions of Instagram sources will fail until you add them again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); remove(); }}
              disabled={deleting}
              className="bg-[#D95D39] hover:bg-[#c14e2e] text-white"
              data-testid="ig-confirm-delete-btn"
            >
              {deleting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="mt-6 text-xs text-[#6A6F5C] leading-relaxed">
        Tip: sessionid cookies typically last several weeks. If extractions start failing with "sessionid invalid or expired", just log in again on instagram.com and paste a fresh one.
      </div>
    </div>
  );
}
